import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { addToCart, clearProduct, getProductById } from '../action'


function ProductDetail() {

  const dispatch = useDispatch()
  const { id } = useParams()
  const product = useSelector(state => state.product)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)


  useEffect(() => {
    dispatch(clearProduct())
    dispatch(getProductById(id))
    return () => {
      dispatch(clearProduct())
    };
  }, [dispatch, id])

  const handleCart = (id) => {
    dispatch(addToCart(product))
  }

  if (!product) {
    return (
      <section className='h-screen flex justify-center items-center bg-slate-50'>
        <div className="vintage-loading">
          <div className="vintage-spinner"></div>
          <h2 className="mt-6 text-xl font-vintage-display font-semibold text-slate-800">Cargando producto...</h2>
        </div>
      </section>
    )
  }
  const { title, description, category, price, currency, imageUrl, imageUrls } = product
  const resolvedCurrency = currency || 'ARS'
  const displayPrice = Number(price)

  // Crear array de imágenes (priorizar imageUrls, luego imageUrl como fallback)
  const images = imageUrls && imageUrls.length > 0
    ? imageUrls
    : imageUrl
      ? [imageUrl]
      : ['https://images.pexels.com/photos/1464625/pexels-photo-1464625.jpeg?auto=compress&cs=tinysrgb&w=400'];

  // Función para obtener URL completa de imagen
  const getImageSrc = (url) => {
    if (!url) return 'https://images.pexels.com/photos/1464625/pexels-photo-1464625.jpeg?auto=compress&cs=tinysrgb&w=400';
    return url.startsWith('http') ? url : `http://localhost:4200${url}`;
  };

  // Funciones del carrusel
  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  return (
  <section className='pt-32 pb-16 lg:py-32 min-h-screen bg-slate-50'>
      <div className='container mx-auto px-6'>
        <div className='flex flex-col lg:flex-row items-center gap-12 max-w-6xl mx-auto'>

          {/* Galería de Imágenes Vintage */}
          <div className='flex flex-1 justify-center items-center'>
            <div className='relative w-full max-w-[400px] lg:max-w-[500px]'>
              {/* Imagen principal vintage */}
              <div className='vintage-product-card relative overflow-hidden bg-gradient-to-br from-white to-slate-50 p-4 flex items-center justify-center'>
                <div className="flex items-center justify-center w-full h-[350px] sm:h-[400px] lg:h-[500px]">
                  <img
                    className='max-w-full max-h-full object-contain transition-all duration-500 hover:scale-105'
                    src={getImageSrc(images[currentImageIndex])}
                    alt={`${title} - imagen ${currentImageIndex + 1}`}
                  />
                </div>

                {/* Botones de navegación vintage */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className='absolute left-4 top-1/2 transform -translate-y-1/2 vintage-filter-btn p-3 group bg-white/90'
                    >
                      <svg className='w-5 h-5 group-hover:scale-110 transition-transform' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
                      </svg>
                    </button>
                    <button
                      onClick={nextImage}
                      className='absolute right-4 top-1/2 transform -translate-y-1/2 vintage-filter-btn p-3 group bg-white/90'
                    >
                      <svg className='w-5 h-5 group-hover:scale-110 transition-transform' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                      </svg>
                    </button>
                  </>
                )}

                {/* Indicador de imagen actual vintage */}
                {images.length > 1 && (
                  <div className='absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-slate-900/80 text-white px-3 py-1 rounded text-xs font-medium'>
                    {currentImageIndex + 1} / {images.length}
                  </div>
                )}
              </div>

              {/* Miniaturas vintage */}
              {images.length > 1 && (
                <div className='flex justify-center mt-6 gap-3 overflow-x-auto pb-2'>
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => goToImage(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden transition-all duration-300 hover:scale-105 ${index === currentImageIndex
                          ? 'border-slate-800 ring-2 ring-slate-300/40 shadow-lg'
                          : 'border-slate-200 hover:border-slate-400'
                        }`}
                    >
                      <img
                        src={getImageSrc(image)}
                        alt={`${title} miniatura ${index + 1}`}
                        className='w-full h-full object-cover'
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Información del producto vintage */}
          <div className='flex-1 space-y-8 lg:pl-8'>
            {/* Breadcrumb vintage */}
            <div className="flex items-center space-x-2 text-sm">
              <span className="vintage-category">Inicio</span>
              <span className="text-slate-400">→</span>
              <span className="vintage-category">{category?.name || 'Productos'}</span>
              <span className="text-slate-400">→</span>
              <span className="text-slate-600">{title}</span>
            </div>

            {/* Título vintage */}
            <div>
              <h1 className='font-vintage-display text-3xl lg:text-4xl font-bold text-slate-900 mb-3 leading-tight'>
                {title}
              </h1>
              <div className="w-16 h-1 bg-slate-300 mb-6"></div>
            </div>

            {/* Precio */}
            <div className='mb-8'>
              <span className='price-old text-lg'>
                {resolvedCurrency === 'USD' ? 'US$' : '$'}{Number(displayPrice * 2).toLocaleString('es-AR')}
              </span>
              <span className='vintage-price text-3xl lg:text-4xl mb-2 block'>
                {resolvedCurrency === 'USD' ? 'US$' : '$'}{Number(displayPrice).toLocaleString('es-AR')}
              </span>
              <span className='text-sm text-slate-500'>{resolvedCurrency}</span>
            </div>

            {/* Descripción vintage */}
            <div className='mb-10'>
              <h3 className="font-vintage-display text-lg font-semibold text-slate-800 mb-4">Descripción</h3>
              <p className='text-slate-700 leading-relaxed text-lg'>
                {description || 'Producto seleccionado para acompañar las necesidades de tu negocio con calidad y disponibilidad inmediata.'}
              </p>
            </div>

            {/* Detalles del producto */}
            <div className="bg-white p-6 rounded-lg border border-slate-200 space-y-4 mb-8">
              <h3 className="font-vintage-display text-lg font-semibold text-slate-800">Detalles del producto</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Categoría:</span>
                  <span className="font-medium text-slate-800">{category?.name || 'General'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Estado:</span>
                  <span className="font-medium text-emerald-600">Disponible</span>
                </div>
                
              </div>
            </div>

            {/* Botón de compra vintage */}
            <div className="space-y-4">
              <button
                onClick={handleCart}
                className='btn-vintage w-full py-4 text-lg font-medium transform hover:scale-105 shadow-lg hover:shadow-xl'
              >
                Agregar al Carrito
              </button>

              <div className="flex items-center justify-center space-x-4 text-sm text-slate-600">
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Envío seguro
                </span>
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                    <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                  </svg>
                  Pago seguro
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>

    </section>
  )
}

export default ProductDetail